import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { parseCustomerEmails } from './demCustomers';
import type { DemNotifyOrder } from '../types/dem';

export type DemEmailTemplates = {
  pending_subject: string;
  pending_body: string;
  processed_subject: string;
  processed_body: string;
};

const DEFAULT_TEMPLATES: DemEmailTemplates = {
  pending_subject: 'Order Confirmation - {po_number}',
  pending_body: 'Dear Customer,\n\nWe have received your order {order_id}.\n\n{order_details_table}',
  processed_subject: 'Order Processed - {po_number}',
  processed_body: 'Dear Customer,\n\nYour order {order_id} is now being prepared.\n\n{order_details_table}',
};

type CustomerNotifyProfile = {
  email: string;
  schedule: string;
};

function formatDeliverySchedule(allowedDays: unknown): string {
  let days: number[] = [];
  if (Array.isArray(allowedDays)) {
    days = allowedDays.map((d) => Number(d)).filter((d) => !Number.isNaN(d));
  } else if (typeof allowedDays === 'string' && allowedDays.trim()) {
    try {
      const parsed = JSON.parse(allowedDays) as unknown;
      if (Array.isArray(parsed)) {
        days = parsed.map((d) => Number(d)).filter((d) => !Number.isNaN(d));
      }
    } catch {
      days = [];
    }
  }

  const daysMap: Record<number, string> = {
    0: 'Mon',
    1: 'Tue',
    2: 'Wed',
    3: 'Thu',
    4: 'Fri',
    5: 'Sat',
    6: 'Sun',
  };

  if (days.length === 0) return 'No Delivery Schedule';
  if (days.length === 7) return 'Everyday';
  return days
    .sort((a, b) => a - b)
    .map((d) => daysMap[d] ?? '')
    .filter(Boolean)
    .join(', ');
}

export async function fetchEmailTemplates(): Promise<DemEmailTemplates> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('system_config').select('data').eq('key', 'email_templates').maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));

  const payload = (data?.data ?? {}) as Partial<DemEmailTemplates>;
  return {
    pending_subject: payload.pending_subject ?? DEFAULT_TEMPLATES.pending_subject,
    pending_body: payload.pending_body ?? DEFAULT_TEMPLATES.pending_body,
    processed_subject: payload.processed_subject ?? DEFAULT_TEMPLATES.processed_subject,
    processed_body: payload.processed_body ?? DEFAULT_TEMPLATES.processed_body,
  };
}

async function fetchCustomerNotifyProfile(customerCode: string): Promise<CustomerNotifyProfile> {
  if (!customerCode) {
    return { email: '', schedule: 'No Delivery Schedule' };
  }

  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('customers')
    .select('email, allowed_days')
    .eq('code', customerCode)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));

  const row = data as Record<string, unknown> | null;
  return {
    email: String(row?.email ?? '').trim(),
    schedule: formatDeliverySchedule(row?.allowed_days),
  };
}

function resolvePoNumber(order: DemNotifyOrder): string {
  const raw = order.ref_no?.trim() ?? '';
  if (!raw || raw === '—' || raw.toUpperCase() === 'N/A') return order.order_id;
  return raw;
}

function buildOrderDetailsTable(order: DemNotifyOrder): string {
  const itemsHtml = order.items
    .map((item) => {
      const code = item.item_code || '—';
      const desc = item.description || '—';
      return `<tr><td style="padding: 8px; border: 1px solid #dfe6e9; font-size: 13px;">${code}</td><td style="padding: 8px; border: 1px solid #dfe6e9; font-size: 13px;">${desc}</td><td style="padding: 8px; border: 1px solid #dfe6e9; text-align: center; font-size: 13px;">${item.qty}</td><td style="padding: 8px; border: 1px solid #dfe6e9; text-align: center; font-size: 13px;">${item.uom || '—'}</td></tr>`;
    })
    .join('');

  return `<div style="font-family: 'Segoe UI', Arial, sans-serif; color: #2d3436; margin: 20px 0; background-color: #f8f9fa; padding: 15px; border-radius: 8px; border: 1px solid #dfe6e9;"><p style="margin: 0 0 15px 0; font-size: 14px;"><b>Customer:</b> ${order.customer_name}<br><b>PO Number:</b> ${resolvePoNumber(order)}<br><b>Delivery Date:</b> ${order.delivery_date_display}</p><table style="width: 100%; max-width: 800px; border-collapse: collapse; border: 1px solid #dfe6e9; background-color: white;"><thead><tr style="background-color: #e3f2fd; text-align: left;"><th style="padding: 10px; border: 1px solid #dfe6e9; color: #1565c0; font-size: 13px;">Item Code</th><th style="padding: 10px; border: 1px solid #dfe6e9; color: #1565c0; font-size: 13px;">Description</th><th style="padding: 10px; border: 1px solid #dfe6e9; color: #1565c0; text-align: center; font-size: 13px;">Qty</th><th style="padding: 10px; border: 1px solid #dfe6e9; color: #1565c0; text-align: center; font-size: 13px;">UOM</th></tr></thead><tbody>${itemsHtml}</tbody></table></div>`;
}

function applyTemplate(template: string, order: DemNotifyOrder, tableHtml: string, schedule: string): string {
  const po = resolvePoNumber(order);
  return template
    .replaceAll('{order_id}', order.order_id)
    .replaceAll('{po_number}', po)
    .replaceAll('{delivery_schedule}', schedule)
    .replaceAll('{order_details_table}', tableHtml);
}

function uniqueEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const email of emails) {
    const trimmed = email.trim();
    const key = trimmed.toLowerCase();
    if (!key.includes('@')) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/** Order email not in Customers → both; blank or already in Customers → Customers only. */
export function resolveNotificationRecipients(orderEmails: string[], customerEmails: string[]): string[] {
  const fromOrder = uniqueEmails(orderEmails);
  const fromCustomer = uniqueEmails(customerEmails);

  if (fromOrder.length === 0) {
    return fromCustomer;
  }

  const customerKeys = new Set(fromCustomer.map((email) => email.toLowerCase()));
  const orderHasEmailOutsideCustomer = fromOrder.some((email) => !customerKeys.has(email.toLowerCase()));

  if (!orderHasEmailOutsideCustomer) {
    return fromCustomer;
  }

  return uniqueEmails([...fromOrder, ...fromCustomer]);
}

async function resolveOrderNotificationTargets(
  order: DemNotifyOrder,
): Promise<{ recipients: string[]; schedule: string }> {
  const profile = await fetchCustomerNotifyProfile(order.customer_code);
  const recipients = resolveNotificationRecipients(parseCustomerEmails(order.email), parseCustomerEmails(profile.email));
  return { recipients, schedule: profile.schedule };
}

async function sendEmailViaProxy(to: string[], subject: string, html: string): Promise<void> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error(
      'Email proxy key not configured in the web app build (VITE_DEM_EMAIL_PROXY_KEY).',
    );
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-DEM-Email-Key': proxyKey,
  };

  const res = await fetch('/order-email-api', {
    method: 'POST',
    headers,
    body: JSON.stringify({ to, subject, html }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(formatEmailProxyError(text, res.status));
  }
}

export function formatEmailProxyError(text: string, status: number): string {
  const trimmed = text.trim();
  if (!trimmed) {
    if (status === 401) {
      return 'Email proxy unauthorized. Rebuild with VITE_DEM_EMAIL_PROXY_KEY and ensure it matches server DEM_EMAIL_PROXY_KEY.';
    }
    return `Email proxy failed (HTTP ${status}).`;
  }

  try {
    const json = JSON.parse(trimmed) as { error?: string; detail?: string };
    if (json.error === 'Unauthorized') {
      return 'Email proxy unauthorized. Rebuild with VITE_DEM_EMAIL_PROXY_KEY and ensure it matches server DEM_EMAIL_PROXY_KEY.';
    }
    if (json.error === 'Email proxy is not configured on the server') {
      return 'Email proxy not configured on server (M365 credentials missing).';
    }
    if (json.error === 'Graph sendMail failed') {
      return 'Microsoft Graph rejected the email send request.';
    }
    if (json.error === 'Missing to, subject, or html') {
      return 'Email payload rejected by server (no valid recipient address).';
    }
    if (json.error) return String(json.error);
  } catch {
    // Plain text response
  }

  return trimmed.length > 160 ? `${trimmed.slice(0, 160)}…` : trimmed;
}

function summarizeOrderIds(orderIds: string[]): string {
  if (orderIds.length <= 4) return orderIds.join(', ');
  return `${orderIds.slice(0, 3).join(', ')}, and ${orderIds.length - 3} more`;
}

export type CloudExportStatusBanner = {
  tone: 'ok' | 'warn';
  lines: string[];
};

export function formatCloudExportStatusBanner(
  exported: number,
  notifications: ProcessedNotificationResult,
  orderLabel = 'web order',
): CloudExportStatusBanner {
  const orderNoun = exported === 1 ? orderLabel : `${orderLabel}s`;
  const lines: string[] = [`Pushed ${exported} ${orderNoun} to cloud export queue.`];

  if (notifications.sent > 0) {
    lines.push(`Processed emails sent: ${notifications.sent}.`);
  } else if (notifications.failed.length === 0 && notifications.skippedNoEmail === 0) {
    lines.push('Processed emails sent: 0.');
  }

  if (notifications.skippedNoEmail > 0) {
    lines.push(
      `${notifications.skippedNoEmail} order${notifications.skippedNoEmail === 1 ? '' : 's'} skipped (no customer email).`,
    );
  }

  if (notifications.failed.length > 0) {
    const byReason = new Map<string, string[]>();
    for (const failure of notifications.failed) {
      const ids = byReason.get(failure.reason) ?? [];
      ids.push(failure.order_id);
      byReason.set(failure.reason, ids);
    }

    for (const [reason, orderIds] of byReason) {
      if (orderIds.length === 1) {
        lines.push(`Email failed for ${orderIds[0]}: ${reason}`);
      } else {
        lines.push(`Email failed for ${orderIds.length} orders: ${reason}`);
        lines.push(`Affected: ${summarizeOrderIds(orderIds)}`);
      }
    }
  }

  const tone: CloudExportStatusBanner['tone'] =
    notifications.failed.length > 0 || notifications.skippedNoEmail > 0 ? 'warn' : 'ok';

  return { tone, lines };
}

export type ProcessedNotificationResult = {
  sent: number;
  skippedNoEmail: number;
  failed: { order_id: string; reason: string }[];
};

export type PendingNotificationResult = ProcessedNotificationResult;

export async function sendPendingOrderNotifications(orders: DemNotifyOrder[]): Promise<PendingNotificationResult> {
  const templates = await fetchEmailTemplates();
  const result: PendingNotificationResult = { sent: 0, skippedNoEmail: 0, failed: [] };
  let blockedReason: string | null = null;

  for (const order of orders) {
    if (blockedReason) {
      result.failed.push({ order_id: order.order_id, reason: blockedReason });
      continue;
    }

    try {
      const { recipients, schedule } = await resolveOrderNotificationTargets(order);
      if (recipients.length === 0) {
        result.skippedNoEmail += 1;
        continue;
      }

      const tableHtml = buildOrderDetailsTable(order);
      const subject = applyTemplate(templates.pending_subject, order, tableHtml, schedule);
      const bodyHtml = applyTemplate(templates.pending_body, order, tableHtml, schedule).replace(
        /\n/g,
        '<br>',
      );
      const html = `<html><body><div style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; color: #333;">${bodyHtml}</div></body></html>`;

      await sendEmailViaProxy(recipients, subject, html);
      result.sent += 1;
    } catch (e) {
      const reason = e instanceof Error ? e.message : 'Failed to send notification.';
      result.failed.push({
        order_id: order.order_id,
        reason,
      });
      if (/unauthorized|proxy key not configured|not configured on the server|no valid recipient/i.test(reason)) {
        blockedReason = reason;
      }
    }
  }

  return result;
}

export async function sendProcessedOrderNotifications(orders: DemNotifyOrder[]): Promise<ProcessedNotificationResult> {
  const templates = await fetchEmailTemplates();
  const result: ProcessedNotificationResult = { sent: 0, skippedNoEmail: 0, failed: [] };
  let blockedReason: string | null = null;

  for (const order of orders) {
    if (blockedReason) {
      result.failed.push({ order_id: order.order_id, reason: blockedReason });
      continue;
    }

    try {
      const { recipients, schedule } = await resolveOrderNotificationTargets(order);
      if (recipients.length === 0) {
        result.skippedNoEmail += 1;
        continue;
      }

      const tableHtml = buildOrderDetailsTable(order);
      const subject = applyTemplate(templates.processed_subject, order, tableHtml, schedule);
      const bodyHtml = applyTemplate(templates.processed_body, order, tableHtml, schedule).replace(
        /\n/g,
        '<br>',
      );
      const html = `<html><body><div style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; color: #333;">${bodyHtml}</div></body></html>`;

      await sendEmailViaProxy(recipients, subject, html);
      result.sent += 1;
    } catch (e) {
      const reason = e instanceof Error ? e.message : 'Failed to send notification.';
      result.failed.push({
        order_id: order.order_id,
        reason,
      });
      if (/unauthorized|proxy key not configured|not configured on the server|no valid recipient/i.test(reason)) {
        blockedReason = reason;
      }
    }
  }

  return result;
}
